/**
 * ZYNX Enterprise Audio & Sound Engine
 * Synthesizes crystal-clear UI audio effects directly via Web Audio API.
 * 100% offline, zero network latency, zero missing asset errors.
 */

let audioCtx: AudioContext | null = null;

// Browsers require a user gesture before background notification audio can run.
export function unlockNotificationAudio(): void {
  try { getAudioContext(); } catch { /* Audio is optional on unsupported devices. */ }
}

function getAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!audioCtx) {
    const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (AudioContextClass) {
      audioCtx = new AudioContextClass();
    }
  }
  if (audioCtx && audioCtx.state === 'suspended') {
    audioCtx.resume().catch(() => {});
  }
  return audioCtx;
}

const SOUND_ENABLED_KEY = 'zynx.sound.enabled';

export function isSoundEnabled(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return localStorage.getItem(SOUND_ENABLED_KEY) !== '0';
  } catch {
    return true;
  }
}

export function setSoundEnabled(enabled: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(SOUND_ENABLED_KEY, enabled ? '1' : '0');
  } catch {
    // Storage can be unavailable in private browsing; sound remains optional.
  }
}

/**
 * Plays a pleasant, two-tone crystal notification chime.
 * Tone 1: 659.25 Hz (E5) -> Tone 2: 880 Hz (A5) with soft harmonic overtone.
 */
export function playNotificationSound(force = false): void {
  if (!force && !isSoundEnabled()) return;

  try {
    const ctx = getAudioContext();
    if (!ctx) return;

    const now = ctx.currentTime;

    // Master volume gain
    const masterGain = ctx.createGain();
    masterGain.gain.setValueAtTime(0.18, now);
    masterGain.connect(ctx.destination);

    // Primary Chime Tone 1 (659.25 Hz)
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(659.25, now);
    gain1.gain.setValueAtTime(0, now);
    gain1.gain.linearRampToValueAtTime(0.7, now + 0.02);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.35);

    osc1.connect(gain1);
    gain1.connect(masterGain);
    osc1.start(now);
    osc1.stop(now + 0.36);

    // Primary Chime Tone 2 (880 Hz - slightly delayed for bell bounce)
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(880.0, now + 0.08);
    gain2.gain.setValueAtTime(0, now + 0.08);
    gain2.gain.linearRampToValueAtTime(0.9, now + 0.10);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.55);

    osc2.connect(gain2);
    gain2.connect(masterGain);
    osc2.start(now + 0.08);
    osc2.stop(now + 0.56);

    // Harmonic Sparkle / Resonance (1318.5 Hz - E6)
    const osc3 = ctx.createOscillator();
    const gain3 = ctx.createGain();
    osc3.type = 'triangle';
    osc3.frequency.setValueAtTime(1318.5, now + 0.09);
    gain3.gain.setValueAtTime(0, now + 0.09);
    gain3.gain.linearRampToValueAtTime(0.25, now + 0.11);
    gain3.gain.exponentialRampToValueAtTime(0.001, now + 0.45);

    osc3.connect(gain3);
    gain3.connect(masterGain);
    osc3.start(now + 0.09);
    osc3.stop(now + 0.46);
  } catch (err) {
    console.warn('[ZYNX Audio] Notification chime playback error:', err);
  }
}

/**
 * Plays a soft positive action confirmation chime.
 */
export function playSuccessSound(): void {
  if (!isSoundEnabled()) return;

  try {
    const ctx = getAudioContext();
    if (!ctx) return;

    const now = ctx.currentTime;
    const masterGain = ctx.createGain();
    masterGain.gain.setValueAtTime(0.15, now);
    masterGain.connect(ctx.destination);

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(523.25, now); // C5
    osc.frequency.exponentialRampToValueAtTime(783.99, now + 0.15); // G5

    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.8, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);

    osc.connect(gain);
    gain.connect(masterGain);
    osc.start(now);
    osc.stop(now + 0.31);
  } catch (err) {
    console.warn('[ZYNX Audio] Success chime error:', err);
  }
}
